import { useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function useIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [sources, setSources] = useState([]);
  const [expanded, setExpanded] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const generate = useCallback(async (filters) => {
    setLoading(true); setError(""); setIdeas([]); setSources([]); setExpanded({});
    try {
      const payload = {
        category: filters.category !== "Any" ? filters.category : null,
        budget: filters.budget !== "Any" ? filters.budget : null,
        skill_level: filters.skill !== "Any" ? filters.skill : null,
        market_size: filters.market !== "Any" ? filters.market : null,
        country: filters.country || null,
        currency: filters.currency || null,
        count: filters.count,
      };
      const { data } = await api.post("/ideas/generate", payload);
      setIdeas(data.ideas);
      setSources(data.sources || []);
      toast.success(`${data.ideas.length} ideas generated`);
      return { ok: true };
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402) {
        // Free-tier exceeded — caller should open paywall.
        return { ok: false, paywall: true, info: detail };
      }
      const msg = typeof detail === "string" ? detail : (e?.message || "Generation failed");
      setError(msg);
      toast.error("Generation failed");
      return { ok: false };
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleExpand = useCallback((id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  }, []);

  return { ideas, sources, expanded, loading, error, generate, toggleExpand };
}

export function useSavedIdeas() {
  const [saved, setSaved] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/ideas/saved");
      setSaved(data);
    } catch {
      // ignore transient load errors
    }
  }, []);

  const saveIdea = useCallback(async (idea) => {
    try {
      const { data } = await api.post("/ideas/save", { idea });
      setSaved((prev) => [data, ...prev]);
      toast.success("Idea saved");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      let msg = "Failed to save";
      if (status === 401) msg = "Session expired — please log in again";
      else if (detail) msg = `Failed to save: ${detail}`;
      else if (e?.message) msg = `Failed to save: ${e.message}`;
      toast.error(msg);
    }
  }, []);

  const deleteSaved = useCallback(async (savedId) => {
    try {
      await api.delete(`/ideas/saved/${savedId}`);
      setSaved((prev) => prev.filter((s) => s.id !== savedId));
      toast.success("Removed");
    } catch {
      toast.error("Failed to delete");
    }
  }, []);

  return { saved, load, saveIdea, deleteSaved };
}
